/**
 * ============================================================================
 * DailyReport 模块 - 班长日报类型定义
 * ============================================================================
 *
 * 📋 文件职责：
 * - 班长日报的响应、请求、查询与常量类型
 * - 后端对应：app/Modules/Production/Controllers/DailyReportController.php
 *
 * ⚠️ 口径说明：
 * - 日报内的产量与人数为班组长填报口径，与报工事实表的产量统计相互独立
 *
 * @module types/production/daily-report
 * @last-updated 2026-09-20
 * ============================================================================
 */

// ============================================================================
// 枚举取值
// ============================================================================

/** 班次取值 */
export type DailyReportShift = 'morning' | 'afternoon' | 'night';

/** 状态取值 */
export type DailyReportStatus = 'draft' | 'submitted';

/** 统计趋势粒度 */
export type DailyReportGranularity = 'day' | 'week' | 'month';

/** 班次文案映射 */
export const DAILY_REPORT_SHIFT_LABELS: Record<DailyReportShift, string> = {
  morning: '早班',
  afternoon: '中班',
  night: '夜班',
};

/** 状态文案映射 */
export const DAILY_REPORT_STATUS_LABELS: Record<DailyReportStatus, string> = {
  draft: '草稿',
  submitted: '已提交',
};

/** 班次下拉选项 */
export const DAILY_REPORT_SHIFT_OPTIONS: Array<{ label: string; value: DailyReportShift }> = [
  { label: DAILY_REPORT_SHIFT_LABELS.morning, value: 'morning' },
  { label: DAILY_REPORT_SHIFT_LABELS.afternoon, value: 'afternoon' },
  { label: DAILY_REPORT_SHIFT_LABELS.night, value: 'night' },
];

/** 状态下拉选项 */
export const DAILY_REPORT_STATUS_OPTIONS: Array<{ label: string; value: DailyReportStatus }> = [
  { label: DAILY_REPORT_STATUS_LABELS.draft, value: 'draft' },
  { label: DAILY_REPORT_STATUS_LABELS.submitted, value: 'submitted' },
];

// ============================================================================
// 响应类型
// ============================================================================

/**
 * 班长日报
 *
 * 对应后端 DailyReportResource::toArray()
 */
export interface DailyReport {
  // ===== 基本信息 =====
  /** 日报ID */
  id: number;

  /** 日报单号 */
  reportNo: string;

  /** 日报日期 */
  reportDate: string;

  /** 班次 */
  shift: DailyReportShift;

  /** 班次文案 */
  shiftText: string;

  /** 部门ID（由班组自动带出） */
  departmentId?: number | null;

  /** 部门名称 */
  departmentName?: string | null;

  /** 班组ID（组织架构班组） */
  teamId?: number | null;

  /** 班组名称（后端写路径统一装载，创建/更新/确认响应稳定返回） */
  teamName?: string | null;

  // ===== 填报人 =====
  /** 填报账号ID */
  userId: number;

  /** 填报人员工ID */
  employeeId?: number | null;

  /** 填报人姓名 */
  reporterName?: string | null;

  /** 录入人账号ID（代填时与填报人不同） */
  createdBy?: number | null;

  /** 录入人姓名 */
  creatorName?: string | null;

  /** 是否代填（录入人与填报人不同） */
  delegated?: boolean;

  // ===== 人员出勤 =====
  /** 应到人数 */
  plannedAttendeeCount?: number | null;

  /** 实到人数 */
  actualAttendeeCount?: number | null;

  /** 出勤率（百分数） */
  attendanceRate: number;

  // ===== 产量与质量 =====
  /** 计划产量 */
  plannedOutput: number;

  /** 实际产量 */
  actualOutput: number;

  /** 不良数量 */
  defectiveOutput: number;

  /** 合格数量 */
  qualifiedOutput: number;

  /** 合格率（百分数） */
  qualifiedRate: number;

  // ===== 异常与交接 =====
  /** 设备异常说明 */
  equipmentIssue?: string | null;

  /** 质量异常说明 */
  qualityIssue?: string | null;

  /** 交接班事项 */
  handoverNote?: string | null;

  /** 交接接收人账号ID */
  handoverConfirmedBy?: number | null;

  /** 交接接收人姓名 */
  handoverConfirmedName?: string | null;

  /** 交接接收时间 */
  handoverConfirmedAt?: string | null;

  /** 关联安灯呼叫ID */
  andonCallId?: number | null;

  /** 关联NCR单ID */
  ncrOrderId?: number | null;

  /** 备注 */
  remark?: string | null;

  // ===== 状态与审计 =====
  /** 状态 */
  status: DailyReportStatus;

  /** 状态文案 */
  statusText: string;

  /** 提交时间 */
  submittedAt?: string | null;

  /** 创建时间 */
  createdAt?: string | null;

  /** 更新时间 */
  updatedAt?: string | null;

  /** 当前用户是否可确认接收交接（详情接口返回） */
  canReceiveHandover?: boolean;

  /** 操作留痕（详情接口返回，最近 20 条） */
  auditLogs?: DailyReportAuditLog[];
}

/** 日报操作留痕 */
export interface DailyReportAuditLog {
  /** 动作：create / update / delete / confirm-handover 等 */
  action: string;

  /** 描述 */
  description?: string | null;

  /** 操作人姓名 */
  operatorName?: string | null;

  /** 操作时间 */
  createdAt?: string | null;
}

/** 上一班次交接事项（填报辅助） */
export interface DailyReportPreviousHandover {
  /** 上一班日报ID */
  id: number;

  /** 日报单号 */
  reportNo: string;

  /** 日报日期 */
  reportDate: string;

  /** 班次 */
  shift: DailyReportShift;

  /** 班次文案 */
  shiftText: string;

  /** 填报人姓名 */
  reporterName?: string | null;

  /** 交接事项 */
  handoverNote?: string | null;

  /** 接收人姓名 */
  handoverConfirmedName?: string | null;

  /** 接收时间 */
  handoverConfirmedAt?: string | null;
}

/** 重复填报软提示 */
export interface DailyReportDuplicateCheck {
  /** 同班组该班次是否已有他人提交 */
  duplicated: boolean;

  /** 已提交人姓名（命中时返回） */
  reporterName?: string | null;

  /** 提交时间（命中时返回） */
  submittedAt?: string | null;
}

/** 日报统计汇总 */
export interface DailyReportStatisticsSummary {
  /** 日报条数 */
  reportCount: number;

  /** 计划产量合计 */
  plannedOutput: number;

  /** 实际产量合计 */
  actualOutput: number;

  /** 不良数量合计 */
  defectiveOutput: number;

  /** 合格数量合计 */
  qualifiedOutput: number;

  /** 合格率 */
  qualifiedRate: number;

  /** 应到人数合计 */
  plannedAttendeeCount: number;

  /** 实到人数合计 */
  actualAttendeeCount: number;

  /** 出勤率 */
  attendanceRate: number;
}

/** 按日期的统计行 */
export interface DailyReportStatisticsByDate {
  /** 日报日期 */
  reportDate: string;

  /** 日报条数 */
  reportCount: number;

  /** 计划产量 */
  plannedOutput: number;

  /** 实际产量 */
  actualOutput: number;

  /** 不良数量 */
  defectiveOutput: number;

  /** 合格数量 */
  qualifiedOutput: number;

  /** 合格率 */
  qualifiedRate: number;

  /** 出勤率 */
  attendanceRate: number;
}

/** 按班组的统计行 */
export interface DailyReportStatisticsByDepartment {
  /** 班组（部门）ID */
  departmentId?: number | null;

  /** 班组名称 */
  departmentName?: string | null;

  /** 日报条数 */
  reportCount: number;

  /** 计划产量 */
  plannedOutput: number;

  /** 实际产量 */
  actualOutput: number;

  /** 不良数量 */
  defectiveOutput: number;

  /** 合格数量 */
  qualifiedOutput: number;

  /** 合格率 */
  qualifiedRate: number;
}

/** 按班次的统计行 */
export interface DailyReportStatisticsByShift {
  /** 班次 */
  shift: DailyReportShift;

  /** 日报条数 */
  reportCount: number;

  /** 计划产量 */
  plannedOutput: number;

  /** 实际产量 */
  actualOutput: number;

  /** 不良数量 */
  defectiveOutput: number;
}

/** 按周/月聚合的统计行 */
export interface DailyReportStatisticsByPeriod {
  /** 周期标识：周 `2026-W38` / 月 `2026-09` */
  period: string;

  /** 周期起始日期 */
  periodStart: string;

  /** 日报条数 */
  reportCount: number;

  /** 计划产量 */
  plannedOutput: number;

  /** 实际产量 */
  actualOutput: number;

  /** 不良数量 */
  defectiveOutput: number;

  /** 合格数量 */
  qualifiedOutput: number;

  /** 合格率 */
  qualifiedRate: number;

  /** 出勤率 */
  attendanceRate: number;
}

/** 日报统计响应 */
export interface DailyReportStatistics {
  /** 趋势粒度 */
  granularity: DailyReportGranularity;

  /** 汇总 */
  summary: DailyReportStatisticsSummary;

  /** 按日期聚合（granularity=day 时返回） */
  byDate?: DailyReportStatisticsByDate[];

  /** 按周/月聚合（granularity=week/month 时返回） */
  byPeriod?: DailyReportStatisticsByPeriod[];

  /** 按班组聚合 */
  byDepartment: DailyReportStatisticsByDepartment[];

  /** 按班次聚合 */
  byShift: DailyReportStatisticsByShift[];

  /** 统计起始日期 */
  reportDateFrom: string;

  /** 统计结束日期 */
  reportDateTo: string;
}

// ============================================================================
// 查询参数类型
// ============================================================================

/**
 * 日报列表查询参数
 *
 * 对应后端: DailyReportController::index()（字段白名单见 FILTER_FIELDS）
 */
export interface DailyReportQueryParams {
  // ===== 分页参数 =====
  /** 页码 */
  page?: number;

  /** 每页数量（1-100） */
  per_page?: number;

  // ===== 筛选参数 =====
  /** 关键字（日报单号 / 填报人姓名） */
  keyword?: string;

  /** 班次 */
  shift?: DailyReportShift;

  /** 部门ID */
  department_id?: number;

  /** 班组ID */
  team_id?: number;

  /** 填报账号ID（筛选条件；可见范围按部门树收敛，非仅本人） */
  user_id?: number;

  /** 状态 */
  status?: DailyReportStatus;

  /** 交接接收筛选：1 已接收 / 0 待接收（待接收=已提交且未接收） */
  handover_received?: 0 | 1;

  /** 日报日期（起始） */
  report_date_from?: string;

  /** 日报日期（结束） */
  report_date_to?: string;

  // ===== 排序参数 =====
  /** 排序字段（report_no / report_date / status / created_at；默认 report_date desc + id desc） */
  sort_by?: "created_at" | "report_date" | "report_no" | "status" | string;

  /** 排序顺序 */
  sort_order?: "asc" | "desc";
}

/**
 * 日报统计查询参数
 *
 * 缺省返回近 30 天，跨度上限 90 天
 */
export interface DailyReportStatisticsQueryParams {
  /** 日报日期（起始） */
  report_date_from?: string;

  /** 日报日期（结束） */
  report_date_to?: string;

  /** 班组（部门）ID */
  department_id?: number;

  /** 班次 */
  shift?: DailyReportShift;

  /** 趋势粒度：day 日（默认）/ week 周 / month 月 */
  granularity?: DailyReportGranularity;
}

// ============================================================================
// 请求类型
// ============================================================================

/**
 * 创建班长日报表单
 *
 * 对应后端 StoreDailyReportRequest
 * 必填（2026-09-20 需求确认）：report_date、shift、team_id、reporter_user_id（缺省本人）、handover_note
 */
export interface CreateDailyReportForm {
  /** 日报日期（必填，不能晚于今天） */
  report_date: string;

  /** 班次（必填） */
  shift: DailyReportShift;

  /** 班组ID（必填，组织架构班组；部门由班组自动带出） */
  team_id: number;

  /** 填报人账号ID（缺省为登录人；代填仅限管理人员） */
  reporter_user_id?: number;

  /** 应到人数 */
  planned_attendee_count?: number | null;

  /** 实到人数 */
  actual_attendee_count?: number | null;

  /** 计划产量 */
  planned_output?: number;

  /** 实际产量 */
  actual_output?: number;

  /** 不良数量（不得超过实际产量） */
  defective_output?: number;

  /** 设备异常说明 */
  equipment_issue?: string | null;

  /** 质量异常说明 */
  quality_issue?: string | null;

  /** 交接班事项（必填） */
  handover_note: string;

  /** 关联安灯呼叫ID */
  andon_call_id?: number | null;

  /** 关联NCR单ID */
  ncr_order_id?: number | null;

  /** 备注 */
  remark?: string | null;

  /** 提交状态：draft 存草稿 / submitted 直接提交 */
  status?: DailyReportStatus;
}

/**
 * 修改班长日报表单
 *
 * 对应后端 UpdateDailyReportRequest（仅提交的字段被修改）。
 * 填报人归属（reporter_user_id）不可修改，后端不接受该字段；
 * 产量不变量（defective_output ≤ actual_output）由后端在合并持久值后统一校验，
 * 三个产量字段显式传 null 按 0 处理（数据库列为非空，与创建口径一致）。
 */
export type UpdateDailyReportForm = Partial<
  Omit<CreateDailyReportForm, 'reporter_user_id'>
>;

// ============================================================================
// 今日日报汇总（管理者视角，2026-09-21 新增）
// ============================================================================

/** 今日已提交班次条目 */
export interface DailyReportTodaySubmittedShift {
  /** 日报ID（深链跳详情用） */
  reportId: number;

  /** 日报单号 */
  reportNo: string;

  /** 班次：morning / afternoon / night */
  shift: DailyReportShift;

  /** 班次文案 */
  shiftText: string;

  /** 填报人姓名 */
  reporterName: string | null;

  /** 提交时间 */
  submittedAt: string | null;

  /** 实际产量 */
  actualOutput: number;

  /** 不良数 */
  defectiveOutput: number;

  /** 是否存在设备异常 */
  hasEquipmentIssue: boolean;

  /** 是否存在质量异常 */
  hasQualityIssue: boolean;
}

/** 今日汇总班组行 */
export interface DailyReportTodayTeam {
  /** 班组ID */
  teamId: number;

  /** 班组名称 */
  teamName: string;

  /** 负责人 */
  managerName: string | null;

  /** 已提交班次列表 */
  submittedShifts: DailyReportTodaySubmittedShift[];

  /** 未交班次取值 */
  missingShifts: DailyReportShift[];
}

/** 今日汇总车间分组 */
export interface DailyReportTodayWorkshop {
  /** 车间ID（未挂车间或「未归类日报」分组时为 null） */
  workshopId: number | null;

  /**
   * 车间名称；「未归类日报」分组固定为该文案——
   * 承载不属于任何可见班组的已提交日报（无部门/无班组、班组停用或不可见、
   * 历史 team_id 为空），保证 summary.reportCount 与分组明细一致
   */
  workshopName: string | null;

  /** 车间下班组行 */
  teams: DailyReportTodayTeam[];
}

/** 今日汇总汇总数字 */
export interface DailyReportTodaySummary {
  /** 应交班组数（可见范围内启用班组） */
  expectedTeamCount: number;

  /** 已交班组数（今日有已提交日报的班组） */
  submittedTeamCount: number;

  /** 未交班组数（今日无任何提交的班组） */
  missingTeamCount: number;

  /** 日报条数 */
  reportCount: number;

  /** 计划产量合计 */
  plannedOutput: number;

  /** 实际产量合计 */
  actualOutput: number;

  /** 不良数合计 */
  defectiveOutput: number;

  /** 合格产量 */
  qualifiedOutput: number;

  /** 合格率（百分比，一位小数） */
  qualifiedRate: number;

  /** 出勤率（百分比，一位小数） */
  attendanceRate: number;
}

/** 今日日报汇总响应 */
export interface DailyReportTodayOverview {
  /** 汇总日期（YYYY-MM-DD） */
  reportDate: string;

  /** 汇总数字 */
  summary: DailyReportTodaySummary;

  /** 按车间分组的班组列表 */
  workshops: DailyReportTodayWorkshop[];
}

/** 日报推送开关响应 */
export interface DailyReportNotificationSwitch {
  /** 是否开启日报推送（缺省视为开启） */
  enabled: boolean;
}
